import React, { useState } from 'react';
import axios from 'axios';
import { History, ChevronDown, ChevronUp } from 'lucide-react';
import { StageBadge, money } from './VisitBits';
import { formatCATDateTime } from '../../utils/timezone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PatientHistory({ serial, authCfg, currentVisitId }) {
  const [open, setOpen] = useState(false);
  const [visits, setVisits] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    if (!open && visits === null) {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/visits/patient/${encodeURIComponent(serial)}/history`, authCfg);
        setVisits((res.data.visits || []).filter((v) => v.visit_id !== currentVisitId));
      } catch (e) {
        setVisits([]);
      } finally {
        setLoading(false);
      }
    }
    setOpen(!open);
  };

  const past = visits || [];

  return (
    <div className="border border-violet-200 rounded-lg bg-violet-50/50">
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between p-3 text-sm font-semibold text-violet-900 hover:bg-violet-100/60 rounded-lg transition-colors"
        data-testid="patient-history-toggle"
      >
        <span className="flex items-center gap-2">
          <History className="w-4 h-4" /> Patient Visit History{visits !== null ? ` (${past.length})` : ''}
        </span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2" data-testid="patient-history-list">
          {loading && <p className="text-sm text-gray-500">Loading history…</p>}
          {!loading && past.length === 0 && (
            <p className="text-sm text-gray-500" data-testid="patient-history-empty">No previous visits for this patient.</p>
          )}
          {past.map((v) => {
            const completedTests = (v.lab_orders || []).filter((o) => o.status === 'completed');
            const dispensedMeds = (v.prescriptions || []).filter((p) => p.status === 'dispensed');
            const removedItems = [...(v.lab_orders || []), ...(v.prescriptions || [])].filter((i) => i.status === 'removed');
            return (
            <div key={v.visit_id} className="bg-white border border-gray-200 rounded-lg p-3 text-sm" data-testid={`history-visit-${v.visit_id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="font-semibold text-gray-800">{formatCATDateTime(v.checked_in_at)}</span>
                <span className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">{v.hospital_name}</span>
                  <StageBadge status={v.status} />
                </span>
              </div>
              {v.consultation?.complaints && <p className="text-gray-700 mt-1"><span className="font-medium">Complaints:</span> {v.consultation.complaints}</p>}
              {(v.consultation?.provisional_diagnosis || v.consultation?.diagnosis) && <p className="text-gray-700"><span className="font-medium">Provisional Dx:</span> {v.consultation.provisional_diagnosis || v.consultation.diagnosis}</p>}
              {v.consultation?.final_diagnosis && <p className="text-gray-700"><span className="font-medium">Final Dx:</span> {v.consultation.final_diagnosis}</p>}
              {completedTests.length > 0 && (
                <p className="text-gray-600 mt-1 break-words [overflow-wrap:anywhere]">                  <span className="font-medium">Tests:</span>{' '}
                  {completedTests.map((o) => `${o.item_name}: ${o.result || '—'}`).join(' • ')}
                </p>
              )}
              {dispensedMeds.length > 0 && (
                <p className="text-gray-600">
                  <span className="font-medium">Medications:</span>{' '}
                  {dispensedMeds.map((p) => `${p.item_name} x${p.quantity}${p.instructions ? ` (${p.instructions})` : ''}`).join(' • ')}
                </p>
              )}
              {removedItems.length > 0 && (
                <p className="text-orange-700 mt-1">
                  <span className="font-medium">⚠️ Removed by approver:</span>{' '}
                  {removedItems.map((i) => i.item_name).join(' • ')}
                  {' — '}{removedItems[0].removal_reason || ''}
                </p>
              )}
              {v.billing?.claim_id && (
                <p className="text-xs text-gray-500 mt-1">Claim: {v.billing.claim_id} • {money(v, v.billing.total_amount_local)}</p>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
